import { useState, useEffect } from "react";
import { IPaymentGridProps } from "../../typings/models/paymentgridprops";
import PaymentGridRow from "./PaymentGridRow";
import "../../../src/styles//LabStyles.css";
import Loader from "../../components/molecules/Loader";
const PaymentGrid: React.FC<IPaymentGridProps> = ({
  data,
  setData,
  isLoading,
  headerColumns,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>("paymentDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;
  const sortData = (
    data: any[],
    column: string | null,
    direction: "asc" | "desc"
  ) => {
    if (!column) return data;

    return [...data].sort((a, b) => {
      let aValue = a[column];
      let bValue = b[column];
      if (column === "paymentDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);

        if (isNaN(aValue.getTime())) aValue = new Date(0);
        if (isNaN(bValue.getTime())) bValue = new Date(0);
      }

      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      } else if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      } else {
        return 0;
      }
    });
  };
  const [sortedData, setSortedData] = useState<any[]>(
    sortData(data, "paymentDate", "asc")
  );

  useEffect(() => {
    const sorted = sortData(data, sortColumn, sortDirection);
    setSortedData(sorted);
  }, [data, sortColumn, sortDirection]);

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = filteredData.slice(startIndex, endIndex);
  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };

  return (
    <div className="container">
      <div className="info-content">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearch}
          className="form-control mb-3 search-input"
        />

        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {headerColumns.map((column, index) => (
                <th
                  key={index}
                  className="grid-header"
                  onClick={() => handleSort(column.sortKey)}
                >
                  {column.columnName}
                  {sortColumn === column.sortKey && (
                    <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToShow.map((item, ind) => (
              <PaymentGridRow
                key={ind}
                data={{ ...item, sno: startIndex + ind + 1 }}
                sno={ind + 1}
              />
            ))}
          </tbody>
        </table>
        {isLoading && <Loader />}

        <nav aria-label="Page navigation example">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {renderPageButtons()}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <br />
    </div>
  );
};

export default PaymentGrid;
